<template>
  <div class="autocomplete-container">
    <n-input
      class="autocomplete-input"
      v-model="inputValue"
      v-bind="inputProps"
      :autofocus="autofocus"
      :imageLeft="imageLeft"
      :iconLeft="iconLeft"
      @focus.self="onFocus"
      @input="$emit('input', $event)"
      @blur.self="onBlur"
      @keydown.down.prevent="onArrowDown"
      @keydown.up.prevent="onArrowUp"
      @keydown.enter="onSelect(activeElement)"
      @keydown.esc="onEsc($event.target)"
      @clickIconLeft="$emit('clickIconLeft')"
      ref="input"
    />
    <n-menu ref="dropdown">
      <n-list-item
        v-for="(option, index) in options"
        :key="index"
        :active="isActiveElement(index)"
        @mousedown="onSelect(index)"
        @mouseenter="activeElement = index"
      >
        <slot :option="option">{{ toLabel(option) }}</slot>
      </n-list-item>
    </n-menu>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Ref, Watch } from 'vue-property-decorator'
import NInput from '@/modules/global/components/core/NInput.vue'
import NMenu from '@/modules/global/components/core/NMenu/NMenu.vue'
import tippy, { Instance } from 'tippy.js'

type InputProps = Pick<NInput, 'label' | 'value' | 'disabled' | 'placeholder' | 'error'>

@Component({
  name: 'n-autocomplete'
})
export default class NAutocomplete<T> extends Vue {
  @Prop({ default: undefined }) value?: T
  @Prop({ default: undefined }) label?: InputProps['label']
  @Prop({ default: '' }) placeholder!: InputProps['placeholder']
  @Prop({ default: '' }) input!: string
  @Prop({ default: false }) deleteOnEnter?: boolean
  @Prop({ default: undefined, type: Boolean }) disabled?: InputProps['disabled']
  @Prop({ default: undefined }) error?: InputProps['error']
  @Prop({ default: undefined, type: Boolean }) autofocus?: boolean
  @Prop({ default: undefined }) imageLeft?: string
  @Prop({ default: undefined }) iconLeft?: string

  @Prop({ default: () => [] }) options!: T[]
  @Prop({ default: (opt: T) => opt, type: Function }) toLabel!: (option: T) => string

  @Ref('input') inputRef!: NInput
  @Ref('dropdown') dropdownRef!: NMenu

  private activeElement: number = -1

  private inputValue: string = ''

  currentTippyInstance: Instance<any> | null = null

  private get inputProps(): InputProps {
    return {
      label: this.label,
      placeholder: this.placeholder,
      disabled: this.disabled,
      error: this.error
    }
  }

  isActiveElement(index: number) {
    return this.activeElement === index
  }

  mounted() {
    this.$nextTick(() => {
      this.inputValue = this.input
    })
  }

  private onFocus() {
    this.toggleDropdown(true)
  }

  private onBlur() {
    this.toggleDropdown(false)
    this.activeElement = -1
    this.$emit('blur')
  }

  private onArrowDown() {
    if (!this.isDropdownOpen()) {
      this.activeElement = 0
      this.toggleDropdown(true)
    } else {
      this.activeElement = Math.min(this.activeElement + 1, this.options.length - 1)
    }
  }

  private onArrowUp() {
    this.activeElement = Math.max(this.activeElement - 1, -1)
  }

  private onEsc() {
    this.toggleDropdown(false)
  }

  private onSelect(index: number) {
    if (index < 0 && !this.deleteOnEnter) {
      this.onBlur()
      return
    }

    const selectedValue = index >= 0 ? this.options[index] : this.value

    const inputValue = selectedValue ? this.toLabel(selectedValue) : ''

    this.$emit('input', inputValue)
    this.$emit('select', selectedValue)
    this.$nextTick(() => {
      if (index >= 0) this.$emit('selectEntry', selectedValue)
      else this.$emit('enterValue', this.inputValue)
      this.toggleDropdown(false)
      this.activeElement = -1
      this.inputValue = inputValue
    })
  }

  @Watch('options') watchOptions() {
    if (this.options.length) {
      if (!this.isDropdownOpen() && this.inputRef.isFocused()) {
        this.toggleDropdown(true)
      }
    } else {
      this.toggleDropdown(false)
    }
  }

  toggleDropdown(show: boolean) {
    if (show && this.options.length) {
      this.openDropdown(this.inputRef.$el as HTMLElement)
    } else {
      this.closeDropdown()
      this.activeElement = -1
    }
  }

  clear() {
    this.inputValue = ''
  }

  replaceInput(newInput: string) {
    this.inputValue = newInput
  }

  openDropdown(openFrom: HTMLElement) {
    if (this.isDropdownOpen()) {
      return
    }
    this.currentTippyInstance = tippy('body', {
      appendTo: () => this.$el,
      content: this.dropdownRef.$el,
      showOnCreate: true,
      interactive: true,
      hideOnClick: false,
      theme: 'none',
      trigger: 'manual',
      placement: 'bottom-start',
      getReferenceClientRect: () => openFrom.getBoundingClientRect(),
      maxWidth: '100%',
      zIndex: 1500,
      onCreate: (instance: Instance) => {
        const popperElement = instance.popper as HTMLDivElement
        popperElement.querySelector<HTMLDivElement>(
          '.menu-wrapper'
        )!.style.width = `${this.inputRef?.$el.clientWidth}px`
      }
    })[0]
  }

  closeDropdown() {
    this.currentTippyInstance?.hide()
  }

  isDropdownOpen() {
    return this.currentTippyInstance?.state?.isShown || false
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/core/_spacing.scss' as *;
@use '@/assets/styles/core/_typography.scss' as *;
@use '@/assets/styles/core/_colors.scss' as *;

.autocomplete-container {
  position: relative;
}

.autocomplete-input {
  position: relative;
}

.dropdown-label {
  height: $notch-space-15x;

  &.empty {
    height: 50%;
  }
}

.divider {
  margin: -$notch-space-1x $notch-space-2x 0;

  border-bottom: 2px solid $notch-color-neutral-sand-200;
}

.menu-dropdown {
  transform: translateX(-5px);
}
</style>
